/* V23.7 | No cart requests; Shopify-rendered market prices; native lifecycle handles filtered grids. */
(() => {
  'use strict';
  if (customElements.get('ov-variant-card')) return;
  class OvVariantCard extends HTMLElement {
    connectedCallback() {
      if (this._abort) return;
      this._abort = new AbortController();
      this._raf = requestAnimationFrame(() => this.init());
    }
    disconnectedCallback() {
      this._abort?.abort(); this._abort = null;
      this._resize?.disconnect();
      cancelAnimationFrame(this._raf);
      this._token = (this._token || 0) + 1;
    }
    init() {
      if (!this.isConnected || !this._abort) return;
      this._card = this.querySelector('.product-card-wrapper');
      this._panel = this.querySelector('[data-ovc-panel]');
      this._info = this._card?.querySelector('.card-information');
      this._row = this._panel?.querySelector('.ovc-row');
      this._more = this._panel?.querySelector('[data-ovc-more]');
      if (!this._info || !this._row || !this._more) return;
      this._choices = [...this._panel.querySelectorAll('[data-ovc-choice]')];
      this._status = this._panel.querySelector('[data-ovc-status]');
      const price = this._info.querySelector(':scope > .price');
      if (price) price.after(this._panel); else this._info.append(this._panel);
      this._expanded = false;
      this._token = (this._token || 0) + 1;
      this._choices.forEach(choice => {
        if (!choice.dataset.image) return; // Real named link to PDP when no variant image exists.
        choice.setAttribute('role','button');
        choice.setAttribute('aria-pressed',choice.dataset.initial === 'true' ? 'true' : 'false');
      });
      const options = {signal:this._abort.signal};
      this.addEventListener('click',event => {
        const choice = event.target.closest('[data-ovc-choice]');
        if (!choice || !this._panel.contains(choice) || !choice.dataset.image || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0) return;
        event.preventDefault(); event.stopPropagation(); this.select(choice);
      },options);
      this.addEventListener('keydown',event => {
        const choice = event.target.closest('[data-ovc-choice][data-image]');
        if (choice && event.key === ' ' && this._panel.contains(choice)) {event.preventDefault(); this.select(choice);}
      },options);
      this._more.addEventListener('click',event => {
        event.preventDefault(); event.stopPropagation();
        const wasExpanded = this._expanded;
        this._expanded = !wasExpanded; this.fit();
        if (!wasExpanded) this._choices[this._collapsedCount]?.focus({preventScroll:true});
      },options);
      window.addEventListener('resize',() => this.fit(),options);
      if ('ResizeObserver' in window) {
        this._resize = new ResizeObserver(() => this.fit()); this._resize.observe(this._panel);
      }
      this.setAttribute('data-ready','');
      this.fit();
    }
    fit() {
      if (!this._panel || !this.isConnected) return;
      const width = this._panel.getBoundingClientRect().width;
      if (width <= 0) return;
      const size = this._choices[0]?.getBoundingClientRect().width || parseFloat(getComputedStyle(this._panel).getPropertyValue('--ovc-size')) || 44;
      const mobile = window.matchMedia('(max-width:749.98px)').matches;
      const max = Math.max(1,Number(mobile ? this._panel.dataset.limitMobile : this._panel.dataset.limitDesktop) || (mobile ? 3 : 4));
      const gap = parseFloat(getComputedStyle(this._row).gap) || 0;
      const moreWidth = parseFloat(getComputedStyle(this._more).width) || 36;
      const slots = Math.max(1,Math.floor((width + gap)/(size + gap)));
      const total = this._choices.length;
      let count = Math.min(max,slots,total);
      if (count < total) count = Math.max(1,Math.min(count,Math.floor((width - moreWidth)/(size + gap))));
      this._collapsedCount = count;
      const hiddenCount = total - count;
      this._choices.forEach((choice,index) => {choice.hidden = !this._expanded && index >= count;});
      this._more.hidden = hiddenCount <= 0;
      this._more.textContent = this._expanded ? '−' : `+${hiddenCount}`;
      this._more.setAttribute('aria-expanded',String(this._expanded));
      this._more.setAttribute('aria-label',this._expanded ? 'Mostrar menos opciones' : `Ver ${hiddenCount} opciones más`);
    }
    select(choice) {
      const token = ++this._token;
      this._panel.setAttribute('aria-busy','true');
      const preload = new Image();
      const finish = () => {
        if (token !== this._token || !this.isConnected) return;
        this._panel.removeAttribute('aria-busy');
        this.apply(choice);
      };
      preload.onload = finish;
      preload.onerror = () => {
        if (token !== this._token || !this.isConnected) return;
        this._panel.removeAttribute('aria-busy');
        choice.removeAttribute('data-image'); choice.removeAttribute('role'); choice.removeAttribute('aria-pressed');
        this._status.textContent = 'No se pudo cargar la foto. Toca de nuevo para ver esta opción en el producto.';
      };
      preload.src = choice.dataset.image;
      if (preload.complete && preload.naturalWidth) {preload.onload = null; finish();}
    }
    apply(choice) {
      const media = this._card.querySelector('.card__media .media');
      if (!media) return;
      let image = media.querySelector('[data-ovc-main]') || media.querySelector('img');
      if (!image) {image = document.createElement('img');image.className='motion-reduce';media.prepend(image);}
      media.querySelectorAll('video').forEach(video => video.pause());
      image.setAttribute('data-ovc-main','');
      image.removeAttribute('srcset');
      image.src = choice.dataset.image;
      if (choice.dataset.srcset) image.srcset = choice.dataset.srcset;
      image.sizes = `${Math.ceil(media.getBoundingClientRect().width)}px`;
      image.alt = `${this._card.querySelector('.card__heading')?.textContent.trim() || ''} — ${choice.dataset.label}`;
      this.classList.add('ovc-chosen');
      const price = this._info.querySelector(':scope > .price');
      const nextPrice = choice.querySelector('template[data-ovc-price]')?.content.firstElementChild?.cloneNode(true);
      if (price && nextPrice) price.replaceWith(nextPrice);
      const badge = this._card.querySelector('.card__badge');
      if (badge) {
        badge.querySelectorAll('[data-ov-badge]').forEach(node => node.remove());
        const saving = Number(choice.dataset.saving);
        if (saving > 0) {
          const node = document.createElement('span');
          node.className='ov-sale ov-badge--sale'; node.dataset.ovBadge='sale';
          node.textContent=`−${saving}%`;node.setAttribute('aria-label',`${saving}% de descuento`);badge.prepend(node);
        }
      }
      this._card.querySelectorAll('.card__heading a[href],a.quick-add__submit[href],.card__media a[href]').forEach(link => {
        const url = new URL(link.getAttribute('href'),document.baseURI);
        url.searchParams.set('variant',choice.dataset.variantId);
        link.href = `${url.pathname}${url.search}${url.hash}`;
      });
      this._choices.forEach(item => {if(item.dataset.image)item.setAttribute('aria-pressed',String(item===choice));});
      this._status.textContent=`${choice.dataset.label} seleccionada. ${nextPrice?.querySelector('.price-item--sale')?.textContent.trim() || ''}`;
      this.dispatchEvent(new CustomEvent('ov:card-variant-change',{bubbles:true,detail:{variantId:choice.dataset.variantId}}));
    }
  }
  customElements.define('ov-variant-card',OvVariantCard);
})();
